import React, { useEffect, useState } from 'react';
import { SessionTypeItem, PlatformItem, PRFeedbackTypeItem, Product } from '../../types';
import { useCrudList } from '../../hooks/useCrudList';
import { MessageSetter } from '../../utils/messages';
import { apiRequest } from '../../utils/api';
import ProductSelector from './ProductSelector';

interface ParametersProps {
  setMessage: MessageSetter;
}

const ParametersSection: React.FC<ParametersProps> = ({ setMessage }) => {
  const [allProducts, setAllProducts] = useState<Product[]>([]);

  // Fetch all products for settings (not filtered by global selection)
  useEffect(() => {
    const fetchAllProducts = async () => {
      try {
        const data = await apiRequest<Product[]>('/api/products', {}, { useAuth: true });
        setAllProducts(data || []);
      } catch (error) {
        console.error('Error fetching products:', error);
        setAllProducts([]);
      }
    };
    fetchAllProducts();
  }, []);

  // Session Types CRUD
  const {
    items: sessionTypes,
    loading: loadingSessionTypes,
    fetchAll: fetchSessionTypes,
    create: createSessionType,
    update: updateSessionType,
    remove: removeSessionType,
  } = useCrudList<SessionTypeItem>({
    endpoint: '/api/sessiontypes',
    useAuth: true,
    setMessage,
  });

  // Platforms CRUD
  const {
    items: platforms,
    loading: loadingPlatforms,
    fetchAll: fetchPlatforms,
    create: createPlatform,
    update: updatePlatform,
    remove: removePlatform,
  } = useCrudList<PlatformItem>({
    endpoint: '/api/platforms',
    useAuth: true,
    setMessage,
  });

  // PR Feedback Comment Types CRUD
  const {
    items: commentTypes,
    loading: loadingCommentTypes,
    fetchAll: fetchCommentTypes,
    create: createCommentType,
    update: updateCommentType,
    remove: removeCommentType,
  } = useCrudList<PRFeedbackTypeItem>({
    endpoint: '/api/prfeedback-comment-types',
    useAuth: true,
    setMessage,
  });

  // PR Feedback Severities CRUD
  const {
    items: severities,
    loading: loadingSeverities,
    fetchAll: fetchSeverities,
    create: createSeverity,
    update: updateSeverity,
    remove: removeSeverity,
  } = useCrudList<PRFeedbackTypeItem>({
    endpoint: '/api/prfeedback-severities',
    useAuth: true,
    setMessage,
  });

  // PR Feedback Impacts CRUD
  const {
    items: impacts,
    loading: loadingImpacts,
    fetchAll: fetchImpacts,
    create: createImpact,
    update: updateImpact,
    remove: removeImpact,
  } = useCrudList<PRFeedbackTypeItem>({
    endpoint: '/api/prfeedback-impacts',
    useAuth: true,
    setMessage,
  });

  const [showSessionTypeModal, setShowSessionTypeModal] = useState(false);
  const [showPlatformModal, setShowPlatformModal] = useState(false);
  const [showCommentTypeModal, setShowCommentTypeModal] = useState(false);
  const [showSeverityModal, setShowSeverityModal] = useState(false);
  const [showImpactModal, setShowImpactModal] = useState(false);
  const [editingSessionType, setEditingSessionType] = useState<string | null>(null);
  const [editingPlatform, setEditingPlatform] = useState<string | null>(null);
  const [editingCommentType, setEditingCommentType] = useState<string | null>(null);
  const [editingSeverity, setEditingSeverity] = useState<string | null>(null);
  const [editingImpact, setEditingImpact] = useState<string | null>(null);
  const [sessionTypeForm, setSessionTypeForm] = useState<{
    name: string;
    displayOrder: number;
    productIds: string[];
  }>({ name: '', displayOrder: 0, productIds: [] });
  const [platformForm, setPlatformForm] = useState<{
    name: string;
    displayOrder: number;
    productIds: string[];
  }>({ name: '', displayOrder: 0, productIds: [] });
  const [commentTypeForm, setCommentTypeForm] = useState<{
    name: string;
    displayOrder: number;
    productIds: string[];
    impactId?: string;
  }>({ name: '', displayOrder: 0, productIds: [], impactId: '' });
  const [severityForm, setSeverityForm] = useState<{
    name: string;
    displayOrder: number;
    productIds: string[];
  }>({ name: '', displayOrder: 0, productIds: [] });
  const [impactForm, setImpactForm] = useState<{
    name: string;
    displayOrder: number;
    productIds: string[];
  }>({ name: '', displayOrder: 0, productIds: [] });

  useEffect(() => {
    fetchSessionTypes();
    fetchPlatforms();
    fetchCommentTypes();
    fetchSeverities();
    fetchImpacts();
  }, [fetchSessionTypes, fetchPlatforms, fetchCommentTypes, fetchSeverities, fetchImpacts]);

  // Prevent background scrolling when modals are open
  useEffect(() => {
    if (showSessionTypeModal || showPlatformModal) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [showSessionTypeModal, showPlatformModal]);

  // Filter session types and platforms based on global product selection
  // In settings, always show all session types and platforms regardless of global product selection
  // Settings should not be affected by the global product filter
  const filteredSessionTypes = sessionTypes;
  const filteredPlatforms = platforms;

  // Helper functions to display products
  const getProductInitials = (product: Product): string => {
    if (product.projectId) {
      return product.projectId.toUpperCase();
    }
    const name = product.name.trim();
    const words = name.split(/\s+/);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    if (name.length >= 2) {
      return (name[0] + name[1]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getProductColor = (name: string): string => {
    const colors = [
      '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981',
      '#6366F1', '#EF4444', '#14B8A6', '#F97316',
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Session Type handlers
  const handleEditSessionType = (sessionType: SessionTypeItem) => {
    setEditingSessionType(sessionType.id);
    setSessionTypeForm({
      name: sessionType.name,
      displayOrder: sessionType.displayOrder || 0,
      productIds: sessionType.productIds || [],
    });
    setShowSessionTypeModal(true);
  };

  const handleOpenNewSessionType = () => {
    setEditingSessionType('new');
    setSessionTypeForm({ name: '', displayOrder: 0, productIds: [] });
    setShowSessionTypeModal(true);
  };

  const resetSessionTypeForm = () => {
    setEditingSessionType(null);
    setSessionTypeForm({ name: '', displayOrder: 0, productIds: [] });
    setShowSessionTypeModal(false);
  };

  const handleSaveSessionType = async () => {
    if (!sessionTypeForm.name.trim()) {
      setMessage({ type: 'error', text: 'Name is required' });
      return;
    }

    try {
      const idToSave = editingSessionType && editingSessionType !== 'new' ? editingSessionType : undefined;
      if (idToSave) {
        await updateSessionType(idToSave, sessionTypeForm, 'Session type updated successfully');
      } else {
        await createSessionType(sessionTypeForm, 'Session type created successfully');
      }
      await fetchSessionTypes();
      resetSessionTypeForm();
    } catch (error) {
      console.error('Error saving session type:', error);
    }
  };

  const handleDeleteSessionType = async (sessionTypeId: string) => {
    if (!window.confirm('Are you sure you want to delete this session type?')) {
      return;
    }

    try {
      await removeSessionType(sessionTypeId, 'Session type deleted successfully');
    } catch (error) {
      console.error('Error deleting session type:', error);
    }
  };

  // Platform handlers
  const handleEditPlatform = (platform: PlatformItem) => {
    setEditingPlatform(platform.id);
    setPlatformForm({
      name: platform.name,
      displayOrder: platform.displayOrder || 0,
      productIds: platform.productIds || [],
    });
    setShowPlatformModal(true);
  };

  const handleOpenNewPlatform = () => {
    setEditingPlatform('new');
    setPlatformForm({ name: '', displayOrder: 0, productIds: [] });
    setShowPlatformModal(true);
  };

  const resetPlatformForm = () => {
    setEditingPlatform(null);
    setPlatformForm({ name: '', displayOrder: 0, productIds: [] });
    setShowPlatformModal(false);
  };

  const handleSavePlatform = async () => {
    if (!platformForm.name.trim()) {
      setMessage({ type: 'error', text: 'Name is required' });
      return;
    }

    try {
      const idToSave = editingPlatform && editingPlatform !== 'new' ? editingPlatform : undefined;
      if (idToSave) {
        await updatePlatform(idToSave, platformForm, 'Platform updated successfully');
      } else {
        await createPlatform(platformForm, 'Platform created successfully');
      }
      await fetchPlatforms();
      resetPlatformForm();
    } catch (error) {
      console.error('Error saving platform:', error);
    }
  };

  const handleDeletePlatform = async (platformId: string) => {
    if (!window.confirm('Are you sure you want to delete this platform?')) {
      return;
    }

    try {
      await removePlatform(platformId, 'Platform deleted successfully');
    } catch (error) {
      console.error('Error deleting platform:', error);
    }
  };

  // PR Feedback Comment Type handlers
  const handleEditCommentType = (commentType: PRFeedbackTypeItem) => {
    setEditingCommentType(commentType.id);
    setCommentTypeForm({
      name: commentType.name,
      displayOrder: commentType.displayOrder || 0,
      productIds: commentType.productIds || [],
      impactId: commentType.impactId || '',
    });
    setShowCommentTypeModal(true);
  };

  const handleOpenNewCommentType = () => {
    setEditingCommentType('new');
    setCommentTypeForm({ name: '', displayOrder: 0, productIds: [], impactId: '' });
    setShowCommentTypeModal(true);
  };

  const resetCommentTypeForm = () => {
    setEditingCommentType(null);
    setCommentTypeForm({ name: '', displayOrder: 0, productIds: [], impactId: '' });
    setShowCommentTypeModal(false);
  };

  const handleSaveCommentType = async () => {
    if (!commentTypeForm.name.trim()) {
      setMessage({ type: 'error', text: 'Name is required' });
      return;
    }

    try {
      const idToSave = editingCommentType && editingCommentType !== 'new' ? editingCommentType : undefined;
      if (idToSave) {
        await updateCommentType(idToSave, commentTypeForm, 'Comment type updated successfully');
      } else {
        await createCommentType(commentTypeForm, 'Comment type created successfully');
      }
      await fetchCommentTypes();
      resetCommentTypeForm();
    } catch (error) {
      console.error('Error saving comment type:', error);
    }
  };

  const handleDeleteCommentType = async (commentTypeId: string) => {
    if (!window.confirm('Are you sure you want to delete this comment type?')) {
      return;
    }

    try {
      await removeCommentType(commentTypeId, 'Comment type deleted successfully');
    } catch (error) {
      console.error('Error deleting comment type:', error);
    }
  };

  // PR Feedback Severity handlers
  const handleEditSeverity = (severity: PRFeedbackTypeItem) => {
    setEditingSeverity(severity.id);
    setSeverityForm({
      name: severity.name,
      displayOrder: severity.displayOrder || 0,
      productIds: severity.productIds || [],
    });
    setShowSeverityModal(true);
  };

  const handleOpenNewSeverity = () => {
    setEditingSeverity('new');
    setSeverityForm({ name: '', displayOrder: 0, productIds: [] });
    setShowSeverityModal(true);
  };

  const resetSeverityForm = () => {
    setEditingSeverity(null);
    setSeverityForm({ name: '', displayOrder: 0, productIds: [] });
    setShowSeverityModal(false);
  };

  const handleSaveSeverity = async () => {
    if (!severityForm.name.trim()) {
      setMessage({ type: 'error', text: 'Name is required' });
      return;
    }

    try {
      const idToSave = editingSeverity && editingSeverity !== 'new' ? editingSeverity : undefined;
      if (idToSave) {
        await updateSeverity(idToSave, severityForm, 'Severity updated successfully');
      } else {
        await createSeverity(severityForm, 'Severity created successfully');
      }
      await fetchSeverities();
      resetSeverityForm();
    } catch (error) {
      console.error('Error saving severity:', error);
    }
  };

  const handleDeleteSeverity = async (severityId: string) => {
    if (!window.confirm('Are you sure you want to delete this severity?')) {
      return;
    }

    try {
      await removeSeverity(severityId, 'Severity deleted successfully');
    } catch (error) {
      console.error('Error deleting severity:', error);
    }
  };

  // PR Feedback Impact handlers
  const handleEditImpact = (impact: PRFeedbackTypeItem) => {
    setEditingImpact(impact.id);
    setImpactForm({
      name: impact.name,
      displayOrder: impact.displayOrder || 0,
      productIds: impact.productIds || [],
    });
    setShowImpactModal(true);
  };

  const handleOpenNewImpact = () => {
    setEditingImpact('new');
    setImpactForm({ name: '', displayOrder: 0, productIds: [] });
    setShowImpactModal(true);
  };

  const resetImpactForm = () => {
    setEditingImpact(null);
    setImpactForm({ name: '', displayOrder: 0, productIds: [] });
    setShowImpactModal(false);
  };

  const handleSaveImpact = async () => {
    if (!impactForm.name.trim()) {
      setMessage({ type: 'error', text: 'Name is required' });
      return;
    }

    try {
      const idToSave = editingImpact && editingImpact !== 'new' ? editingImpact : undefined;
      if (idToSave) {
        await updateImpact(idToSave, impactForm, 'Impact updated successfully');
      } else {
        await createImpact(impactForm, 'Impact created successfully');
      }
      await fetchImpacts();
      resetImpactForm();
    } catch (error) {
      console.error('Error saving impact:', error);
    }
  };

  const handleDeleteImpact = async (impactId: string) => {
    if (!window.confirm('Are you sure you want to delete this impact?')) {
      return;
    }

    try {
      await removeImpact(impactId, 'Impact deleted successfully');
    } catch (error) {
      console.error('Error deleting impact:', error);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      {/* Session Types Section */}
      <div className="card">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--text-primary)' }}>
            Session Types
          </h2>
          {!showSessionTypeModal && (
            <button
              className="btn btn-primary"
              onClick={handleOpenNewSessionType}
            >
              ➕ Add Session Type
            </button>
          )}
        </div>
        <p
          style={{
            color: 'var(--text-secondary)',
            marginBottom: '1.5rem',
            fontSize: '0.9rem',
          }}
        >
          Manage session types that can be selected when creating testing sessions.
        </p>

        {loadingSessionTypes ? (
          <div>Loading...</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {filteredSessionTypes.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📝</div>
                <h3 style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                  No session types configured
                </h3>
                <p style={{ color: 'var(--text-muted)' }}>
                  Add session types to customize the options available when creating sessions.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {filteredSessionTypes.map((st) => (
                  <div
                    key={st.id}
                    className="card"
                    style={{
                      padding: '1rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          color: 'var(--text-primary)',
                          marginBottom: '0.25rem',
                        }}
                      >
                        {st.name}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                        Order: {st.displayOrder || 0}
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                        {st.productIds && st.productIds.length > 0 ? (
                          st.productIds.map(productId => {
                            const product = allProducts.find(p => p.id === productId);
                            if (!product) return null;
                            const initials = getProductInitials(product);
                            const color = getProductColor(product.name);
                            return (
                              <div
                                key={productId}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.375rem',
                                  padding: '0.25rem 0.5rem',
                                  backgroundColor: color,
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  color: '#fff',
                                  fontWeight: 500,
                                }}
                                title={product.name}
                              >
                                <span>{initials}</span>
                                <span>{product.name}</span>
                              </div>
                            );
                          })
                        ) : (
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                            All products
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleEditSessionType(st)}
                        style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleDeleteSessionType(st.id)}
                        style={{
                          fontSize: '0.875rem',
                          padding: '0.5rem 1rem',
                          backgroundColor: '#e74c3c',
                          color: 'white',
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Platforms Section */}
      <div className="card">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--text-primary)' }}>
            Platforms
          </h2>
          {!showPlatformModal && (
            <button
              className="btn btn-primary"
              onClick={handleOpenNewPlatform}
            >
              ➕ Add Platform
            </button>
          )}
        </div>
        <p
          style={{
            color: 'var(--text-secondary)',
            marginBottom: '1.5rem',
            fontSize: '0.9rem',
          }}
        >
          Manage platforms that can be selected when creating testing sessions.
        </p>

        {loadingPlatforms ? (
          <div>Loading...</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {filteredPlatforms.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💻</div>
                <h3 style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                  No platforms configured
                </h3>
                <p style={{ color: 'var(--text-muted)' }}>
                  Add platforms to customize the options available when creating sessions.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {filteredPlatforms.map((p) => (
                  <div
                    key={p.id}
                    className="card"
                    style={{
                      padding: '1rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          color: 'var(--text-primary)',
                          marginBottom: '0.25rem',
                        }}
                      >
                        {p.name}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                        Order: {p.displayOrder || 0}
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                        {p.productIds && p.productIds.length > 0 ? (
                          p.productIds.map(productId => {
                            const product = allProducts.find(pr => pr.id === productId);
                            if (!product) return null;
                            const initials = getProductInitials(product);
                            const color = getProductColor(product.name);
                            return (
                              <div
                                key={productId}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.375rem',
                                  padding: '0.25rem 0.5rem',
                                  backgroundColor: color,
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  color: '#fff',
                                  fontWeight: 500,
                                }}
                                title={product.name}
                              >
                                <span>{initials}</span>
                                <span>{product.name}</span>
                              </div>
                            );
                          })
                        ) : (
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                            All products
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleEditPlatform(p)}
                        style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleDeletePlatform(p.id)}
                        style={{
                          fontSize: '0.875rem',
                          padding: '0.5rem 1rem',
                          backgroundColor: '#e74c3c',
                          color: 'white',
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Session Type Edit Modal */}
      {showSessionTypeModal && (
        <div
          className="modal-overlay"
          onClick={resetSessionTypeForm}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '2rem',
          }}
        >
          <div
            className="card"
            style={{
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              position: 'relative',
              backgroundColor: 'var(--surface)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid var(--grid-line)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--text-primary)' }}>
                {editingSessionType === 'new' ? 'Add New Session Type' : 'Edit Session Type'}
              </h2>
              <button
                className="btn btn-ghost btn-icon"
                onClick={resetSessionTypeForm}
                style={{ fontSize: '1.5rem', padding: '0.5rem' }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                  }}
                >
                  Name *
                </label>
                <input
                  type="text"
                  value={sessionTypeForm.name}
                  onChange={(e) =>
                    setSessionTypeForm({
                      ...sessionTypeForm,
                      name: e.target.value,
                    })
                  }
                  placeholder="e.g., New Feature/Improvement"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    fontSize: '0.875rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                  }}
                >
                  Display Order
                </label>
                <input
                  type="number"
                  value={sessionTypeForm.displayOrder}
                  onChange={(e) =>
                    setSessionTypeForm({
                      ...sessionTypeForm,
                      displayOrder: parseInt(e.target.value) || 0,
                    })
                  }
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    fontSize: '0.875rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                />
              </div>
              <div className="form-group">
                <label>Products</label>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
                  Select products for this session type. Leave all unselected to use for all products.
                </p>
                <ProductSelector
                  selectedProductIds={sessionTypeForm.productIds}
                  onChange={(productIds) => {
                    setSessionTypeForm({
                      ...sessionTypeForm,
                      productIds,
                    });
                  }}
                  products={allProducts}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    await handleSaveSessionType();
                  }}
                >
                  Save
                </button>
                <button className="btn btn-secondary" onClick={resetSessionTypeForm}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Platform Edit Modal */}
      {showPlatformModal && (
        <div
          className="modal-overlay"
          onClick={resetPlatformForm}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '2rem',
          }}
        >
          <div
            className="card"
            style={{
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              position: 'relative',
              backgroundColor: 'var(--surface)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid var(--grid-line)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--text-primary)' }}>
                {editingPlatform === 'new' ? 'Add New Platform' : 'Edit Platform'}
              </h2>
              <button
                className="btn btn-ghost btn-icon"
                onClick={resetPlatformForm}
                style={{ fontSize: '1.5rem', padding: '0.5rem' }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                  }}
                >
                  Name *
                </label>
                <input
                  type="text"
                  value={platformForm.name}
                  onChange={(e) =>
                    setPlatformForm({
                      ...platformForm,
                      name: e.target.value,
                    })
                  }
                  placeholder="e.g., Android"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    fontSize: '0.875rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                  }}
                >
                  Display Order
                </label>
                <input
                  type="number"
                  value={platformForm.displayOrder}
                  onChange={(e) =>
                    setPlatformForm({
                      ...platformForm,
                      displayOrder: parseInt(e.target.value) || 0,
                    })
                  }
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    fontSize: '0.875rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                />
              </div>
              <div className="form-group">
                <label>Products</label>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
                  Select products for this platform. Leave all unselected to use for all products.
                </p>
                <ProductSelector
                  selectedProductIds={platformForm.productIds}
                  onChange={(productIds) => {
                    setPlatformForm({
                      ...platformForm,
                      productIds,
                    });
                  }}
                  products={allProducts}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    await handleSavePlatform();
                  }}
                >
                  Save
                </button>
                <button className="btn btn-secondary" onClick={resetPlatformForm}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PR Feedback Comment Types Section */}
      <div className="card">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--text-primary)' }}>
            PR Feedback Comment Types
          </h2>
          {!showCommentTypeModal && (
            <button
              className="btn btn-primary"
              onClick={handleOpenNewCommentType}
            >
              ➕ Add Comment Type
            </button>
          )}
        </div>
        <p
          style={{
            color: 'var(--text-secondary)',
            marginBottom: '1.5rem',
            fontSize: '0.9rem',
          }}
        >
          Manage comment types that can be selected when creating PR feedback.
        </p>

        {loadingCommentTypes ? (
          <div>Loading...</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {commentTypes.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💬</div>
                <h3 style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                  No comment types configured
                </h3>
                <p style={{ color: 'var(--text-muted)' }}>
                  Add comment types to customize the options available when creating PR feedback.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {commentTypes.map((ct) => (
                  <div
                    key={ct.id}
                    className="card"
                    style={{
                      padding: '1rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          color: 'var(--text-primary)',
                          marginBottom: '0.25rem',
                        }}
                      >
                        {ct.name}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                        Order: {ct.displayOrder || 0}
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                        {ct.productIds && ct.productIds.length > 0 ? (
                          ct.productIds.map(productId => {
                            const product = allProducts.find(p => p.id === productId);
                            if (!product) return null;
                            const initials = getProductInitials(product);
                            const color = getProductColor(product.name);
                            return (
                              <div
                                key={productId}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.375rem',
                                  padding: '0.25rem 0.5rem',
                                  backgroundColor: color,
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  color: '#fff',
                                  fontWeight: 500,
                                }}
                                title={product.name}
                              >
                                <span>{initials}</span>
                                <span>{product.name}</span>
                              </div>
                            );
                          })
                        ) : (
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                            All products
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleEditCommentType(ct)}
                        style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleDeleteCommentType(ct.id)}
                        style={{
                          fontSize: '0.875rem',
                          padding: '0.5rem 1rem',
                          backgroundColor: '#e74c3c',
                          color: 'white',
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* PR Feedback Severities Section */}
      <div className="card">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--text-primary)' }}>
            PR Feedback Severities
          </h2>
          {!showSeverityModal && (
            <button
              className="btn btn-primary"
              onClick={handleOpenNewSeverity}
            >
              ➕ Add Severity
            </button>
          )}
        </div>
        <p
          style={{
            color: 'var(--text-secondary)',
            marginBottom: '1.5rem',
            fontSize: '0.9rem',
          }}
        >
          Manage severity levels that can be selected when creating PR feedback.
        </p>

        {loadingSeverities ? (
          <div>Loading...</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {severities.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
                <h3 style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                  No severities configured
                </h3>
                <p style={{ color: 'var(--text-muted)' }}>
                  Add severities to customize the options available when creating PR feedback.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {severities.map((sev) => (
                  <div
                    key={sev.id}
                    className="card"
                    style={{
                      padding: '1rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          color: 'var(--text-primary)',
                          marginBottom: '0.25rem',
                        }}
                      >
                        {sev.name}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                        Order: {sev.displayOrder || 0}
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                        {sev.productIds && sev.productIds.length > 0 ? (
                          sev.productIds.map(productId => {
                            const product = allProducts.find(p => p.id === productId);
                            if (!product) return null;
                            const initials = getProductInitials(product);
                            const color = getProductColor(product.name);
                            return (
                              <div
                                key={productId}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.375rem',
                                  padding: '0.25rem 0.5rem',
                                  backgroundColor: color,
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  color: '#fff',
                                  fontWeight: 500,
                                }}
                                title={product.name}
                              >
                                <span>{initials}</span>
                                <span>{product.name}</span>
                              </div>
                            );
                          })
                        ) : (
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                            All products
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleEditSeverity(sev)}
                        style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleDeleteSeverity(sev.id)}
                        style={{
                          fontSize: '0.875rem',
                          padding: '0.5rem 1rem',
                          backgroundColor: '#e74c3c',
                          color: 'white',
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* PR Feedback Impacts Section */}
      <div className="card">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--text-primary)' }}>
            PR Feedback Impacts
          </h2>
          {!showImpactModal && (
            <button
              className="btn btn-primary"
              onClick={handleOpenNewImpact}
            >
              ➕ Add Impact
            </button>
          )}
        </div>
        <p
          style={{
            color: 'var(--text-secondary)',
            marginBottom: '1.5rem',
            fontSize: '0.9rem',
          }}
        >
          Manage impact levels that can be selected when creating PR feedback.
        </p>

        {loadingImpacts ? (
          <div>Loading...</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {impacts.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
                <h3 style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                  No impacts configured
                </h3>
                <p style={{ color: 'var(--text-muted)' }}>
                  Add impacts to customize the options available when creating PR feedback.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {impacts.map((imp) => (
                  <div
                    key={imp.id}
                    className="card"
                    style={{
                      padding: '1rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          color: 'var(--text-primary)',
                          marginBottom: '0.25rem',
                        }}
                      >
                        {imp.name}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                        Order: {imp.displayOrder || 0}
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                        {imp.productIds && imp.productIds.length > 0 ? (
                          imp.productIds.map(productId => {
                            const product = allProducts.find(p => p.id === productId);
                            if (!product) return null;
                            const initials = getProductInitials(product);
                            const color = getProductColor(product.name);
                            return (
                              <div
                                key={productId}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.375rem',
                                  padding: '0.25rem 0.5rem',
                                  backgroundColor: color,
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  color: '#fff',
                                  fontWeight: 500,
                                }}
                                title={product.name}
                              >
                                <span>{initials}</span>
                                <span>{product.name}</span>
                              </div>
                            );
                          })
                        ) : (
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                            All products
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleEditImpact(imp)}
                        style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleDeleteImpact(imp.id)}
                        style={{
                          fontSize: '0.875rem',
                          padding: '0.5rem 1rem',
                          backgroundColor: '#e74c3c',
                          color: 'white',
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* PR Feedback Comment Type Edit Modal */}
      {showCommentTypeModal && (
        <div
          className="modal-overlay"
          onClick={resetCommentTypeForm}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '2rem',
          }}
        >
          <div
            className="card"
            style={{
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              position: 'relative',
              backgroundColor: 'var(--surface)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid var(--grid-line)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--text-primary)' }}>
                {editingCommentType === 'new' ? 'Add New Comment Type' : 'Edit Comment Type'}
              </h2>
              <button
                className="btn btn-ghost btn-icon"
                onClick={resetCommentTypeForm}
                style={{ fontSize: '1.5rem', padding: '0.5rem' }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                  }}
                >
                  Name *
                </label>
                <input
                  type="text"
                  value={commentTypeForm.name}
                  onChange={(e) =>
                    setCommentTypeForm({
                      ...commentTypeForm,
                      name: e.target.value,
                    })
                  }
                  placeholder="e.g., bug"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    fontSize: '0.875rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                  }}
                >
                  Display Order
                </label>
                <input
                  type="number"
                  value={commentTypeForm.displayOrder}
                  onChange={(e) =>
                    setCommentTypeForm({
                      ...commentTypeForm,
                      displayOrder: parseInt(e.target.value) || 0,
                    })
                  }
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    fontSize: '0.875rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                  }}
                >
                  Associated Impact
                </label>
                <select
                  value={commentTypeForm.impactId || ''}
                  onChange={(e) =>
                    setCommentTypeForm({
                      ...commentTypeForm,
                      impactId: e.target.value || undefined,
                    })
                  }
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    fontSize: '0.875rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                >
                  <option value="">None (no default impact)</option>
                  {impacts.map((imp) => (
                    <option key={imp.id} value={imp.id}>
                      {imp.name}
                    </option>
                  ))}
                </select>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                  Select an impact type to associate with this comment type. When users select this comment type, the impact will be automatically set.
                </p>
              </div>
              <div className="form-group">
                <label>Products</label>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
                  Select products for this comment type. Leave all unselected to use for all products.
                </p>
                <ProductSelector
                  selectedProductIds={commentTypeForm.productIds}
                  onChange={(productIds) => {
                    setCommentTypeForm({
                      ...commentTypeForm,
                      productIds,
                    });
                  }}
                  products={allProducts}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    await handleSaveCommentType();
                  }}
                >
                  Save
                </button>
                <button className="btn btn-secondary" onClick={resetCommentTypeForm}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PR Feedback Severity Edit Modal */}
      {showSeverityModal && (
        <div
          className="modal-overlay"
          onClick={resetSeverityForm}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '2rem',
          }}
        >
          <div
            className="card"
            style={{
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              position: 'relative',
              backgroundColor: 'var(--surface)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid var(--grid-line)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--text-primary)' }}>
                {editingSeverity === 'new' ? 'Add New Severity' : 'Edit Severity'}
              </h2>
              <button
                className="btn btn-ghost btn-icon"
                onClick={resetSeverityForm}
                style={{ fontSize: '1.5rem', padding: '0.5rem' }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                  }}
                >
                  Name *
                </label>
                <input
                  type="text"
                  value={severityForm.name}
                  onChange={(e) =>
                    setSeverityForm({
                      ...severityForm,
                      name: e.target.value,
                    })
                  }
                  placeholder="e.g., critical"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    fontSize: '0.875rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                  }}
                >
                  Display Order
                </label>
                <input
                  type="number"
                  value={severityForm.displayOrder}
                  onChange={(e) =>
                    setSeverityForm({
                      ...severityForm,
                      displayOrder: parseInt(e.target.value) || 0,
                    })
                  }
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    fontSize: '0.875rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                />
              </div>
              <div className="form-group">
                <label>Products</label>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
                  Select products for this severity. Leave all unselected to use for all products.
                </p>
                <ProductSelector
                  selectedProductIds={severityForm.productIds}
                  onChange={(productIds) => {
                    setSeverityForm({
                      ...severityForm,
                      productIds,
                    });
                  }}
                  products={allProducts}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    await handleSaveSeverity();
                  }}
                >
                  Save
                </button>
                <button className="btn btn-secondary" onClick={resetSeverityForm}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PR Feedback Impact Edit Modal */}
      {showImpactModal && (
        <div
          className="modal-overlay"
          onClick={resetImpactForm}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '2rem',
          }}
        >
          <div
            className="card"
            style={{
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              position: 'relative',
              backgroundColor: 'var(--surface)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid var(--grid-line)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--text-primary)' }}>
                {editingImpact === 'new' ? 'Add New Impact' : 'Edit Impact'}
              </h2>
              <button
                className="btn btn-ghost btn-icon"
                onClick={resetImpactForm}
                style={{ fontSize: '1.5rem', padding: '0.5rem' }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                  }}
                >
                  Name *
                </label>
                <input
                  type="text"
                  value={impactForm.name}
                  onChange={(e) =>
                    setImpactForm({
                      ...impactForm,
                      name: e.target.value,
                    })
                  }
                  placeholder="e.g., production-blocker"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    fontSize: '0.875rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                  }}
                >
                  Display Order
                </label>
                <input
                  type="number"
                  value={impactForm.displayOrder}
                  onChange={(e) =>
                    setImpactForm({
                      ...impactForm,
                      displayOrder: parseInt(e.target.value) || 0,
                    })
                  }
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    fontSize: '0.875rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                />
              </div>
              <div className="form-group">
                <label>Products</label>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
                  Select products for this impact. Leave all unselected to use for all products.
                </p>
                <ProductSelector
                  selectedProductIds={impactForm.productIds}
                  onChange={(productIds) => {
                    setImpactForm({
                      ...impactForm,
                      productIds,
                    });
                  }}
                  products={allProducts}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    await handleSaveImpact();
                  }}
                >
                  Save
                </button>
                <button className="btn btn-secondary" onClick={resetImpactForm}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParametersSection;
